import { EntitiesSchema } from "./Entity";
import { DrawStatus } from "@/enums/DrawStatus";
import { FileType } from "@/enums/FileType";
import { z } from "zod";
import { PortalDrawItemSchema } from "./DrawItem";

export const SelectedDrawSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  drawNumber: z.number(),
  drawStatus: z.nativeEnum(DrawStatus),
  created: z.coerce.date(),
  approved: z.coerce.date().nullable(),
  inspection: z.coerce.date().nullable(),
  percentComplete: z.number(),
  drawNotes: z.string().nullable(),
  inspInstructions: z.string(),
  inspComments: z.string(),
  drawSubmitted: z.boolean(),
  inspectionSubmitted: z.boolean(),
  items: z.array(PortalDrawItemSchema),
  entities: z.array(EntitiesSchema),
});

export const DrawFileSchema = z.object({
  filename: z.string(),
  created: z.string(),
  projectId: z.string(),
  draw: z.number(),
  type: z.nativeEnum(FileType).nullable(),
});

export const DrawFileListSchema = z.array(DrawFileSchema);

export const DrawTimelineItemSchema = z.object({
  drawId: z.string(),
  drawNumber: z.number(),
  dateCreated: z.coerce.date(),
  dateApproved: z.coerce.date().nullable(),
  name: z.string(),
});

export const DrawTimelineSchema = z.array(DrawTimelineItemSchema);

export type DrawTimelineItem = z.infer<typeof DrawTimelineItemSchema>;
export type DrawFile = z.infer<typeof DrawFileSchema>;
export type SelectedDraw = z.infer<typeof SelectedDrawSchema>;
export type PortalDrawItem = z.infer<typeof PortalDrawItemSchema>;

//                                                     _____  _____
//                                                    <     `/     |
//                                                     >          (
//                                                    |   _     _  |
//                                                    |  |_) | |_) |
//                                                    |  | \ | |   |
//                                                    |            |
//                                     ______.______%_|            |__________  _____
//                                   _/                                       \|     |
//                                  |                R E T A I N A G E               <
//                                  |_____.-._________              ____/|___________|
//                                                    | * ??/??/?? |
//                                                    | + 08/07/24 |
//                                                    |            |
//                                                    |            |
//                                                    |   _        <
//                                                    |__/         |
//                                                     / `--.      |
//                                                   %|            |%
//                                               |/.%%|          -< @%%%
//                                               `\%`@|     v      |@@%@%%
//                                             .%%%@@@|%    |    % @@@%%@%%%%
//                                        _.%%%%%%@@@@@@%%_/%\_%@@%%@@@@@@@%%%%%%
//
//
//  ______                  __       __                                                               ______    ______
// |      \                |  \     /  \                                                             /      \  /      \
//  \$$$$$$ _______        | $$\   /  $$  ______   ______ ____    ______    ______   __    __       |  $$$$$$\|  $$$$$$\
//   | $$  |       \       | $$$\ /  $$$ /      \ |      \    \  /      \  /      \ |  \  |  \      | $$  | $$| $$_  \$$
//   | $$  | $$$$$$$\      | $$$$\  $$$$|  $$$$$$\| $$$$$$\$$$$\|  $$$$$$\|  $$$$$$\| $$  | $$      | $$  | $$| $$ \
//   | $$  | $$  | $$      | $$\$$ $$ $$| $$    $$| $$ | $$ | $$| $$  | $$| $$   \$$| $$  | $$      | $$  | $$| $$$$
//  _| $$_ | $$  | $$      | $$ \$$$| $$| $$$$$$$$| $$ | $$ | $$| $$__/ $$| $$      | $$__/ $$      | $$__/ $$| $$
// |   $$ \| $$  | $$      | $$  \$ | $$ \$$     \| $$ | $$ | $$ \$$    $$| $$       \$$    $$       \$$    $$| $$
//  \$$$$$$ \$$   \$$       \$$      \$$  \$$$$$$$ \$$  \$$  \$$  \$$$$$$  \$$       _\$$$$$$$        \$$$$$$  \$$
//                                                                                  |  \__| $$
//                                                                                   \$$    $$
//                                                                                    \$$$$$$
//
//                                           l1RetainagePriorDraw: z.number(),
//                                           l1RetainageThisDraw: z.number(),
//                                           l1ReleasePriorDraw: z.number(),
//                                           l1ReleaseThisDraw: z.number(),
//                                           l2RetainageThisDraw: z.number(),
//                                           l3RetainageThisDraw: z.number(),
//                                           l4RetainageThisDraw: z.number(),
//                                           l5RetainageThisDraw: z.number(),
//                                           l2ReleaseThisDraw: z.number(),
//                                           l3ReleaseThisDraw: z.number(),
//                                           l4ReleaseThisDraw: z.number(),
//                                           l5ReleaseThisDraw: z.number(),
//                                           l1TotalRetainage: z.number(),
//                                           l1TotalReleased: z.number(),
//                                           l1RetainBalance: z.number(),
//                                           l1TotalRetainagePriorDraw: z.number(),
//                                           l1TotalReleasePriorDraw: z.number(),
//                                           l2RetainagePriorDraw: z.number(),
//                                           l2ReleasePriorDraw: z.number(),
//                                           l2TotalRetainage: z.number(),
//                                           l2TotalReleased: z.number(),
//                                           l2RetainBalance: z.number(),
//                                           l2TotalRetainagePriorDraw: z.number(),
//                                           l2TotalReleasePriorDraw: z.number(),
//                                           l3RetainagePriorDraw: z.number(),
//                                           l3ReleasePriorDraw: z.number(),
//                                           l3TotalRetainage: z.number(),
//                                           l3TotalReleased: z.number(),
//                                           l3TotalRetainagePriorDraw: z.number(),
//                                           l3TotalReleasePriorDraw: z.number(),
//                                           l3RetainBalance: z.number(),
//                                           l4RetainagePriorDraw: z.number(),
//                                           l4ReleasePriorDraw: z.number(),
//                                           l4TotalRetainage: z.number(),
//                                           l4TotalReleased: z.number(),
//                                           l4RetainBalance: z.number(),
//                                           l4TotalRetainagePriorDraw: z.number(),
//                                           l4TotalReleasePriorDraw: z.number(),
//                                           l5RetainagePriorDraw: z.number(),
//                                           l5ReleasePriorDraw: z.number(),
//                                           l5TotalRetainage: z.number(),
//                                           l5TotalReleased: z.number(),
//                                           l5RetainBalance: z.number(),
//                                           l5TotalRetainagePriorDraw: z.number(),
//                                           l5TotalReleasePriorDraw: z.number(),
//                                           e1RetainagePriorDraw: z.number(),
//                                           e1RetainageThisDraw: z.number(),
//                                           e1ReleasePriorDraw: z.number(),
//                                           e1ReleaseThisDraw: z.number(),
//                                           e1TotalRetainage: z.number(),
//                                           e1TotalReleased: z.number(),
//                                           e1RetainBalance: z.number(),
//                                           e1TotalRetainagePriorDraw: z.number(),
//                                           e1TotalReleasePriorDraw: z.number(),
//                                           e2RetainagePriorDraw: z.number(),
//                                           e2RetainageThisDraw: z.number(),
//                                           e2ReleasePriorDraw: z.number(),
//                                           e2ReleaseThisDraw: z.number(),
//                                           e2TotalRetainage: z.number(),
//                                           e2TotalReleased: z.number(),
//                                           e2RetainBalance: z.number(),
//                                           e2TotalRetainagePriorDraw: z.number(),
//                                           e2TotalReleasePriorDraw: z.number(),
//                                           e3RetainagePriorDraw: z.number(),
//                                           e3RetainageThisDraw: z.number(),
//                                           e3ReleasePriorDraw: z.number(),
//                                           e3ReleaseThisDraw: z.number(),
//                                           e3TotalRetainage: z.number(),
//                                           e3TotalReleased: z.number(),
//                                           e3RetainBalance: z.number(),
//                                           e3TotalRetainagePriorDraw: z.number(),
//                                           e3TotalReleasePriorDraw: z.number(),
//                                           e4RetainagePriorDraw: z.number(),
//                                           e4RetainageThisDraw: z.number(),
//                                           e4ReleasePriorDraw: z.number(),
//                                           e4ReleaseThisDraw: z.number(),
//                                           e4TotalRetainage: z.number(),
//                                           e4TotalReleased: z.number(),
//                                           e4RetainBalance: z.number(),
//                                           e4TotalRetainagePriorDraw: z.number(),
//                                           e4TotalReleasePriorDraw: z.number(),
//                                           e5RetainagePriorDraw: z.number(),
//                                           e5RetainageThisDraw: z.number(),
//                                           e5ReleasePriorDraw: z.number(),
//                                           e5ReleaseThisDraw: z.number(),
//                                           e5TotalRetainage: z.number(),
//                                           e5TotalReleased: z.number(),
//                                           e5RetainBalance: z.number(),
//                                           e5TotalRetainagePriorDraw: z.number(),
//                                           e5TotalReleasePriorDraw: z.number(),
